import dayjs from 'dayjs';
import { DspDeliveryCompletedSummaryType } from '../graphql/queries/getDspDeliveryCompletedSummaryList';

/** 与 api-server `PageRequestType.toPageRequest()` 中 MAX_PAGE_SIZE 一致，超出会抛「请求超限」 */
export const DSP_DELIVERY_COMPLETED_SUMMARY_FETCH_PAGE_SIZE = 200;

export const DSP_DELIVERY_COMPLETED_SUMMARY_EXPORT_MAX_ROWS = 20000;
export const DSP_DELIVERY_COMPLETED_SUMMARY_CSV_BUILD_CHUNK_ROWS = 500;

/** 导出全量拉取时隔一段时间再请求下一页，降低触发接口限频 */
export const DSP_DELIVERY_COMPLETED_SUMMARY_EXPORT_FETCH_THROTTLE_MS = 250;

/** 防止 hasNext/totalPages 异常时出现死循环（约为「最大行数/页大小」再加余量） */
export const DSP_DELIVERY_COMPLETED_SUMMARY_EXPORT_MAX_FETCH_PAGES =
    Math.ceil(DSP_DELIVERY_COMPLETED_SUMMARY_EXPORT_MAX_ROWS / DSP_DELIVERY_COMPLETED_SUMMARY_FETCH_PAGE_SIZE) + 10;

export function sleepMs(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export function yieldToMain(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, 0));
}

/** 与列表展示顺序无关，仅导出文件内稳定排序 */
export function compareDspDeliveryCompletedSummaryForExport(
    a: DspDeliveryCompletedSummaryType,
    b: DspDeliveryCompletedSummaryType
): number {
    const dc = Number(b.deliveredCount ?? 0) - Number(a.deliveredCount ?? 0);
    if (dc !== 0) return dc;
    const da = Number(a.driverId ?? 0) - Number(b.driverId ?? 0);
    if (da !== 0) return da;
    const ar = Number(a.areaId ?? 0) - Number(b.areaId ?? 0);
    if (ar !== 0) return ar;
    const dd = String(b.deliveredDate ?? '').localeCompare(String(a.deliveredDate ?? ''));
    if (dd !== 0) return dd;
    return String(a.hubId ?? '').localeCompare(String(b.hubId ?? ''));
}

/**
 * 将可能长时间阻塞主线程的 sort 放到下一 macrotask，便于先完成一帧绘制（按钮 loading 等）。
 * 注意：sort 本身仍是同步的，数据量极大时仍可能出现单次较长任务；彻底避免需 Web Worker 或后端导出。
 */
export function sortDspDeliveryCompletedSummaryInNextTask(items: DspDeliveryCompletedSummaryType[]): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(() => {
            items.sort(compareDspDeliveryCompletedSummaryForExport);
            resolve();
        }, 0);
    });
}

export function escapeCsvCell(val: string | number): string {
    const s = String(val ?? '');
    if (/[",\r\n]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

export function rowToCsvLine(cells: (string | number)[]): string {
    return cells.map(escapeCsvCell).join(',');
}

function triggerCsvDownload(filenameBase: string, blob: Blob) {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filenameBase}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export function downloadDspDeliveryCompletedSummaryCsvBlob(filenameBase: string, csvBody: string) {
    triggerCsvDownload(filenameBase, new Blob(['\uFEFF' + csvBody], { type: 'text/csv;charset=utf-8;' }));
}

/**
 * 分片写入 Blob，避免 `n 行字符串数组 + 一次巨型 join` 的峰值与单次超长拼接。
 */
export async function downloadDspDeliveryCompletedSummaryCsvAsBlobParts(
    filenameBase: string,
    headerLine: string,
    rows: DspDeliveryCompletedSummaryType[],
    chunkRows: number,
    yieldBetweenChunks: () => Promise<void>
): Promise<void> {
    const parts: BlobPart[] = [`\uFEFF${headerLine}`];
    if (rows.length === 0) {
        triggerCsvDownload(filenameBase, new Blob(parts, { type: 'text/csv;charset=utf-8;' }));
        return;
    }
    for (let i = 0; i < rows.length; i += chunkRows) {
        const end = Math.min(i + chunkRows, rows.length);
        const chunk = rows
            .slice(i, end)
            .map((r) => rowToCsvLine(mapDspSummaryRowToCells(r)))
            .join('\n');
        parts.push('\n');
        parts.push(chunk);
        if (end < rows.length) {
            await yieldBetweenChunks();
        }
    }
    triggerCsvDownload(filenameBase, new Blob(parts, { type: 'text/csv;charset=utf-8;' }));
}

export function mapDspSummaryRowToCells(r: DspDeliveryCompletedSummaryType): (string | number)[] {
    return [
        r.deliveredDate ? dayjs(r.deliveredDate).format('YYYY-MM-DD') : '',
        r.dspName ?? '',
        r.dspContact ?? '',
        r.dspFullName ?? '',
        r.driverId ?? '',
        r.driverName ?? '',
        r.driverPhone ?? '',
        r.hubName ?? '',
        r.warehouseName ?? '',
        r.areaName ?? (r.areaId != null ? String(r.areaId) : ''),
        Number(r.deliveredCount ?? 0),
        Number(r.standardRateDeliveredCount ?? 0),
        Number(r.discountRateDeliveredCount ?? 0),
    ];
}
